import {
  HubData,
  LocationFilterItem,
  RepeatTemplateEssential,
  ReportDateFilter,
  ReportUserFilter,
  StickerStateId,
  TimerStickerEntry,
  TimetrackerValue
} from 'yougile-model'
import {DataObjectId} from './data-object'
import {GenReq, GenResp} from './dto'

export const idFromUserEvent = (obj: string): DataObjectId => {
  return obj.split(':')[0] as DataObjectId
}

export const getTaskUserEvents = (): UserEvent['type'][] => [
  'add',
  'delete',
  'restore',
  'rename',
  'move',
  'complete',
  'uncomplete',
  'file',
  'done',
  'undone',
  'executor',
  'deadline',
  'string',
  'sprint',
  'timetracking',
  'stopwatch',
  'timer',
  'numeric',
  'text'
]

export type DeadlineUserEvent = {
  type: 'deadline'
  user: DataObjectId
  obj: DataObjectId
  options: {
    oldDeadline?: number
    oldStartDate?: number
    oldHasTime?: boolean
    newDeadline?: number | ''
    newStartDate?: number
    newHasTime?: boolean
  }
}

export type UserEvent =
  | {type: 'add'; user: DataObjectId; obj: DataObjectId}
  | {type: 'deadline'; user: DataObjectId; obj: DataObjectId}

  // obj: object_id or sticker_id:state_id
  | {type: 'delete'; user: DataObjectId; obj: string; stateName?: string}
  | {type: 'restore'; user: DataObjectId; obj: DataObjectId}
  | {
      type: 'rename'
      user: DataObjectId
      obj: DataObjectId
      oldName: string
      newName: string
    }
  | {
      type: 'move'
      user: DataObjectId
      obj: DataObjectId
      from: DataObjectId
      to: DataObjectId
    }
  | {type: 'complete'; user: DataObjectId; obj: DataObjectId}
  | {type: 'uncomplete'; user: DataObjectId; obj: DataObjectId}
  | {type: 'done'; user: DataObjectId; obj: DataObjectId}
  | {type: 'undone'; user: DataObjectId; obj: DataObjectId}
  | {type: 'message'; user: DataObjectId; obj: DataObjectId; time: number}
  | {
      type: 'add-user-to-project'
      user: DataObjectId
      obj: DataObjectId
      users: DataObjectId[]
    }
  | {
      type: 'executor'
      user: DataObjectId
      obj: DataObjectId
      users: DataObjectId[]
    }
  | {
      type: 'repeat-template'
      user: DataObjectId
      obj: DataObjectId
      oldValue: RepeatTemplateEssential | undefined
      newValue: RepeatTemplateEssential | undefined
    }
  | {
      type: 'string' | 'sprint' | 'numeric' | 'text'
      user: DataObjectId
      obj: DataObjectId
      options: {
        id: DataObjectId
        oldValue?: StickerStateId
        newValue?: StickerStateId
      }
    }
  | {
      type: 'timetracking'
      user: DataObjectId
      obj: DataObjectId
      options: {
        id: DataObjectId
        oldValue?: TimetrackerValue
        newValue?: TimetrackerValue
      }
    }
  | {
      type: 'stopwatch' | 'timer'
      user: DataObjectId
      obj: DataObjectId
      options: {
        id: DataObjectId
        oldValue?: TimerStickerEntry
        newValue?: TimerStickerEntry
      }
    }
  | DeadlineUserEvent
  | {
      type: 'deadlineFailed'
      user: DataObjectId
      obj: DataObjectId
    }
  | {
      type: 'deadlineFailed'
      user: DataObjectId
      obj: DataObjectId
    }
  | {
      type: 'remove-user-from-project'
      user: DataObjectId
      obj: DataObjectId
      users: DataObjectId[]
    }
  | {
      type: 'change-project-roles'
      user: DataObjectId
      obj: DataObjectId
      roleNames: string[]
      oldRoles: HubData['roles']
      newRoles: HubData['roles']
    }
  | {
      type: 'change-project-permissions'
      user: DataObjectId
      obj: DataObjectId
      users: DataObjectId[]
      oldPermissions: HubData['userPermissions']
      newPermissions: HubData['userPermissions']
    }
  | {
      type: 'users'
      user: DataObjectId
      obj: DataObjectId
      users: DataObjectId[]
      action: 'add' | 'delete'
    }
  | {
      type: 'file'
      user: DataObjectId
      obj: DataObjectId
      key: number | 'description'
      urls: string[]
    }

export interface UserEventsByTime {
  [timestamp: number]: UserEvent | undefined
}

export interface UserEventsByDay {
  [day: string]: UserEventsByTime
}

export interface CompanyUserEvents {
  companyId: DataObjectId
  timeIx: number[]
  events: UserEventsByTime
}

export interface UserEventsListReq extends GenReq {
  userId: DataObjectId
  key: string
  companyId: DataObjectId
  from: number
  to: number
}

export interface UserEventsListResp extends GenResp {
  events: UserEventsByTime
  onlineTime: {[userId: string]: number}
}

export type ActionTypeFilter =
  | Omit<UserEvent['type'], 'users'>
  | 'add-user'
  | 'delete-user'

export interface TimelineFilter {
  located?: LocationFilterItem[]
  createdAt?: ReportDateFilter
  createdBy?: ReportUserFilter[]
  action?: ActionTypeFilter[]
  id?: string
  title?: string
  relevantCompanyId?: DataObjectId
}

export interface TimelineFilterBackend {
  /**
   * @deprecated
   * use located instead
   */
  childOf?: DataObjectId[]

  located?: LocationFilterItem[]
  createdAt?: {
    start: number
    end: number
  }
  createdBy?: ReportUserFilter[]
  action?: ActionTypeFilter[]
  id?: string
  title?: string
}

export interface UserEventsListLimitedReq extends GenReq {
  userId: DataObjectId
  key: string
  companyId: DataObjectId
  count: number
  filters: TimelineFilterBackend
  taskId?: DataObjectId
}

export interface UserEventsListLimitedResp extends GenResp {
  events: UserEventsByTime
  isEventsLeft: boolean
}

export const getDefaultCompanyUserEvents = (
  companyId: DataObjectId
): CompanyUserEvents => ({
  companyId,
  timeIx: [],
  events: {}
})
